import { apiClient } from '@shared/utils/api-client';
import type { Banner, CreateBannerDto, UpdateBannerDto, SetActiveDto, CreateStoryDto, Story, AttachLinkDto } from '../types';

export const bannersApi = {
  getAll: async (includeStories: boolean = false, activeOnly: boolean = false): Promise<Banner[]> => {
    const params = new URLSearchParams();
    if (includeStories) params.append('includeStories', 'true');
    if (activeOnly) params.append('activeOnly', 'true');
    const queryString = params.toString();
    const url = queryString ? `/banners?${queryString}` : '/banners';
    return apiClient.get<Banner[]>(url);
  },

  getById: async (id: number): Promise<Banner> => {
    return apiClient.get<Banner>(`/banners/${id}`);
  },

  create: async (data: CreateBannerDto, file?: File, storyFiles?: File[]): Promise<Banner> => {
    const formData = new FormData();
    if (data.title) formData.append('title', data.title);
    if (data.link) formData.append('link', data.link);
    if (data.isActive !== undefined) formData.append('isActive', data.isActive.toString());
    if (file) {
      formData.append('image', file);
    }
    if (storyFiles && storyFiles.length > 0) {
      storyFiles.forEach((storyFile) => {
        formData.append('storyImages', storyFile);
      });
    }
    return apiClient.post<Banner>('/banners', formData);
  },

  update: async (id: number, data: UpdateBannerDto, file?: File, storyFiles?: File[], deletedStoryIds?: number[]): Promise<Banner> => {
    const formData = new FormData();
    if (data.title !== undefined) formData.append('title', data.title);
    // Всегда отправляем link, если он определен (включая пустую строку для удаления)
    if (data.link !== undefined) {
      formData.append('link', data.link);
    }
    if (data.order !== undefined) formData.append('order', data.order.toString());
    if (data.isActive !== undefined) formData.append('isActive', data.isActive.toString());
    if (file) {
      formData.append('image', file);
    }
    if (storyFiles && storyFiles.length > 0) {
      storyFiles.forEach((storyFile) => {
        formData.append('storyImages', storyFile);
      });
    }
    if (deletedStoryIds && deletedStoryIds.length > 0) {
      deletedStoryIds.forEach((storyId) => {
        formData.append('deletedStoryIds', storyId.toString());
      });
    }
    return apiClient.put<Banner>(`/banners/${id}`, formData);
  },

  setActive: async (id: number, data: SetActiveDto): Promise<Banner> => {
    return apiClient.patch<Banner>(`/banners/${id}/active`, data);
  },

  attachLink: async (id: number, data: AttachLinkDto): Promise<Banner> => {
    return apiClient.patch<Banner>(`/banners/${id}/link`, data);
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/banners/${id}`);
  },

  updateOrder: async (items: { id: number; order: number }[]): Promise<Banner[]> => {
    return apiClient.patch<Banner[]>('/banners/order', { items });
  },

  createStory: async (data: CreateStoryDto, file?: File): Promise<Story> => {
    const formData = new FormData();
    formData.append('bannerId', data.bannerId.toString());
    if (data.title) formData.append('title', data.title);
    if (data.link) formData.append('link', data.link);
    if (data.order !== undefined) formData.append('order', data.order.toString());
    if (data.isActive !== undefined) formData.append('isActive', data.isActive.toString());
    if (file) {
      formData.append('image', file);
    }
    return apiClient.post<Story>('/banners/stories', formData);
  },
};

